import { Input } from "antd";
import { TextAreaProps } from "antd/es/input";
import classNames from "classnames";
import { FieldWrapper } from "components/UI/Table/UI/InteractiveCell/components/FieldWrapper.tsx";
import { WithSaveButton } from "components/UI/Table/UI/InteractiveCell/components/WithSaveButton.tsx";
import { useInteractiveCell } from "components/UI/Table/UI/InteractiveCell/hooks/useInteractiveCell.ts";
import { FieldProps } from "components/UI/Table/UI/InteractiveCell/miscs/types.ts";
import { ChangeEvent } from "react";
import { FieldValues } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";

const { TextArea } = Input;

type Props<T extends FieldValues> = TextAreaProps & FieldProps<T>;

export const WithTextArea = <T extends FieldValues = FieldValues>({ formControl, ...props }: Props<T>) => {
    const { onChange, onSave, value, className, ...restProps } = props;

    const {
        setControlledValue,
        resolvedValue,
        isFormControlled,
        handleOnSaveSideEffects,
        handleFocus,
        handleBlur,
        fieldRef,
        error,
    } = useInteractiveCell({
        formControl,
        value,
    });

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setControlledValue(event.target.value);
        onChange?.(event);
    };

    const handleSave = () => {
        handleOnSaveSideEffects();
        onSave?.(resolvedValue);
    };

    return (
        <FieldWrapper
            displayValue={resolvedValue || props.defaultValue}
            ref={fieldRef}
            isError={!!error}
            errorMessage={error?.message as string}
        >
            <WithSaveButton onSave={handleSave}>
                {isFormControlled ? (
                    <FormItem {...(formControl as NonNullable<typeof formControl>)} noStyle>
                        <TextArea
                            {...restProps}
                            className={classNames(className, "!w-full")}
                            onBlur={handleBlur}
                            onFocus={handleFocus}
                        />
                    </FormItem>
                ) : (
                    <TextArea
                        {...restProps}
                        onChange={handleChange}
                        className={classNames(className, "!w-full")}
                        value={resolvedValue as TextAreaProps["value"]}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                    />
                )}
            </WithSaveButton>
        </FieldWrapper>
    );
};
